import { Component, OnInit } from '@angular/core';
import { BaseComponent } from 'src/app/core/abstracts/baseComponent';
import { ConferenceEligibilityService } from 'src/app/core/services/conference-eligibility.service';
import {
  ConferenceApplicationModel,
  ConferenceApplicationAnswerModel,
  ConferenceApplicationReviewModel,
  ConferenceApplicationStatus,
  ConferenceApplicationUtilizationModel,
  ConferenceEligibilityReportFilterModel,
  ConferenceEligibilityReportModel,
  ConferenceEligibilityReportRowModel,
  ConferenceUtilizationStatus,
  ConferenceSelectOptionModel
} from 'src/app/models/conferenceEligibilityModels';
import { AlertService } from 'src/app/shared/services/alert.service';

interface ReviewAction {
  statusID: number;
  label: string;
}

@Component({
  selector: 'app-conference-application-review',
  templateUrl: './conference-application-review.component.html',
  standalone: false
})
export class ConferenceApplicationReviewComponent extends BaseComponent implements OnInit {
  applications: ConferenceApplicationModel[] = [];
  statuses: ConferenceSelectOptionModel[] = [];
  selectedApplication: ConferenceApplicationModel;
  selectedStatusID: number;
  notes = '';
  isOverride = false;
  loading = true;
  saving = false;
  showDetail = false;
  statusFilter: number;
  conferenceFilter = '';
  roleFilter = '';
  submittedDateFilter = '';
  activeTab: 'review' | 'post' | 'history' | 'report' = 'review';
  canReview = false;
  canReport = false;
  showUtilization = false;
  selectedUtilizationApplication: ConferenceApplicationModel;
  utilizationStatusID: number;
  hoursPulledDate = '';
  hoursPulled = 0;
  utilizedHours = 0;
  utilizationDate = '';
  utilizationNotes = '';
  loadingHoursPulled = false;
  historyEmployees: { userID: number; employeeName: string }[] = [];
  historyUserID: number;
  employeeHistory: ConferenceApplicationModel[] = [];
  historyLoading = false;
  reportFilter: ConferenceEligibilityReportFilterModel = {};
  report: ConferenceEligibilityReportModel;
  reportRows: ConferenceEligibilityReportRowModel[] = [];
  reportLoading = false;
  exporting = false;
  readonly utilizationStatuses: ConferenceSelectOptionModel[] = [
    { value: ConferenceUtilizationStatus.Utilized, label: 'Utilized' },
    { value: ConferenceUtilizationStatus.NotUtilized, label: 'Not Utilized' }
  ];

  constructor(
    private conferenceEligibilityService: ConferenceEligibilityService,
    private alertService: AlertService
  ) {
    super();
  }

  ngOnInit() {
    this.load();
  }

  get filteredApplications() {
    const conference = this.conferenceFilter.trim().toLowerCase();
    const role = this.roleFilter.trim().toLowerCase();
    return this.applications.filter(application =>
      (!this.statusFilter || application.statusID === Number(this.statusFilter))
      && (!conference || application.conferenceTitle.toLowerCase().includes(conference))
      && (!role || (application.eligibilitySnapshot?.roleCategoryName || '').toLowerCase().includes(role))
      && (!this.submittedDateFilter || application.submittedDate.substring(0, 10) === this.submittedDateFilter)
    );
  }

  get reviewActions(): ReviewAction[] {
    switch (this.selectedApplication?.statusID) {
      case ConferenceApplicationStatus.PendingHrReview:
        return [
          { statusID: ConferenceApplicationStatus.Approved, label: 'Approve' },
          { statusID: ConferenceApplicationStatus.PendingOversight, label: 'Send to Oversight' },
          { statusID: ConferenceApplicationStatus.PendingCvp, label: 'Send to CVP' },
          { statusID: ConferenceApplicationStatus.Denied, label: 'Deny' }
        ];
      case ConferenceApplicationStatus.PendingOversight:
        return [
          { statusID: ConferenceApplicationStatus.Approved, label: 'Approve' },
          { statusID: ConferenceApplicationStatus.PendingCvp, label: 'Send to CVP' },
          { statusID: ConferenceApplicationStatus.PendingHrReview, label: 'Return to HR' },
          { statusID: ConferenceApplicationStatus.Denied, label: 'Deny' }
        ];
      case ConferenceApplicationStatus.PendingCvp:
        return [
          { statusID: ConferenceApplicationStatus.Approved, label: 'Approve' },
          { statusID: ConferenceApplicationStatus.Denied, label: 'Deny' },
          { statusID: ConferenceApplicationStatus.PendingHrReview, label: 'Return to HR' }
        ];
      default:
        return [];
    }
  }

  get approvedApplications() {
    return this.applications.filter(application => application.statusID === ConferenceApplicationStatus.Approved);
  }

  get hoursRemaining() {
    const deduction = Number(this.utilizationStatusID) === ConferenceUtilizationStatus.Utilized
      ? Number(this.utilizedHours || 0)
      : 0;
    return Number((Number(this.hoursPulled || 0) - deduction).toFixed(2));
  }

  load() {
    this.loading = true;
    this.subs.push(
      this.conferenceEligibilityService.getApplications().subscribe({
        next: response => {
          this.applications = response.applications;
          this.statuses = response.statuses;
          this.canReview = response.canReview;
          this.canReport = response.canReport;
          this.historyEmployees = response.applications
            .map(application => ({ userID: application.userID, employeeName: application.employeeName }))
            .filter((employee, index, employees) => employees.findIndex(item => item.userID === employee.userID) === index)
            .sort((first, second) => first.employeeName.localeCompare(second.employeeName));
          if (!this.canReview && this.canReport) {
            this.activeTab = 'report';
          }
          this.loading = false;
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to load conference applications.'));
          this.loading = false;
        }
      })
    );
  }

  openUtilization(application: ConferenceApplicationModel) {
    this.selectedUtilizationApplication = application;
    const today = new Date().toISOString().substring(0, 10);
    this.utilizationStatusID = application.utilizationStatusID;
    this.hoursPulledDate = application.hoursPulledDate?.substring(0, 10) || today;
    this.hoursPulled = Number(application.hoursPulled || 0);
    this.utilizedHours = application.utilizationStatusID
      ? Number(application.utilizedHours || 0)
      : Number(application.reservedHours || 0);
    this.utilizationDate = application.utilizationDate?.substring(0, 10) || today;
    this.utilizationNotes = '';
    this.showUtilization = true;
    if (!application.hoursPulledDate) {
      this.loadHoursPulled();
    }
  }

  loadHoursPulled() {
    if (!this.selectedUtilizationApplication || !this.hoursPulledDate) {
      return;
    }

    const applicationId = this.selectedUtilizationApplication.id;
    const submittedDate = this.hoursPulledDate;
    this.loadingHoursPulled = true;
    this.subs.push(
      this.conferenceEligibilityService.getApplicationHoursPulled(applicationId, submittedDate).subscribe({
        next: result => {
          if (this.selectedUtilizationApplication?.id === applicationId && this.hoursPulledDate === submittedDate) {
            this.hoursPulled = Number(result.hoursPulled || 0);
          }
          this.loadingHoursPulled = false;
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to calculate available hours.'));
          this.loadingHoursPulled = false;
        }
      })
    );
  }

  saveUtilization() {
    if (!this.utilizationStatusID) {
      this.alertService.error('Select Utilized or Not Utilized.');
      return;
    }
    if (!this.hoursPulledDate || !this.utilizationDate) {
      this.alertService.error('The balance date and outcome date are required.');
      return;
    }
    if (Number(this.utilizationStatusID) === ConferenceUtilizationStatus.Utilized && Number(this.utilizedHours) <= 0) {
      this.alertService.error('Hours utilized must be greater than zero.');
      return;
    }
    if (this.selectedUtilizationApplication.utilizationStatusID && !this.utilizationNotes.trim()) {
      this.alertService.error('A note is required when correcting a post-conference outcome.');
      return;
    }

    const applicationId = this.selectedUtilizationApplication.id;
    const utilization: ConferenceApplicationUtilizationModel = {
      utilizationStatusID: Number(this.utilizationStatusID),
      hoursPulledDate: this.hoursPulledDate,
      utilizedHours: Number(this.utilizationStatusID) === ConferenceUtilizationStatus.Utilized
        ? Number(this.utilizedHours)
        : 0,
      utilizationDate: this.utilizationDate,
      notes: this.utilizationNotes.trim()
    };
    this.saving = true;
    this.subs.push(
      this.conferenceEligibilityService.saveApplicationUtilization(applicationId, utilization).subscribe({
        next: application => {
          const index = this.applications.findIndex(item => item.id === applicationId);
          if (index >= 0) {
            this.applications[index] = application;
          }
          this.saving = false;
          this.showUtilization = false;
          this.alertService.success(`Post-conference outcome saved as ${application.utilizationStatusLabel}.`);
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to save the post-conference outcome.'));
          this.saving = false;
        }
      })
    );
  }

  loadEmployeeHistory() {
    if (!this.historyUserID) {
      this.employeeHistory = [];
      return;
    }

    const userId = Number(this.historyUserID);
    this.historyLoading = true;
    this.subs.push(
      this.conferenceEligibilityService.getEmployeeHistory(userId).subscribe({
        next: applications => {
          if (Number(this.historyUserID) === userId) {
            this.employeeHistory = applications;
          }
          this.historyLoading = false;
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to load employee conference history.'));
          this.historyLoading = false;
        }
      })
    );
  }

  loadReport() {
    if (this.reportFilter.startDate && this.reportFilter.endDate
      && this.reportFilter.endDate < this.reportFilter.startDate) {
      this.alertService.error('The report end date must be on or after the start date.');
      return;
    }

    const submittedFilter = { ...this.reportFilter };
    this.reportLoading = true;
    this.subs.push(
      this.conferenceEligibilityService.getReport(submittedFilter).subscribe({
        next: report => {
          this.report = report;
          this.reportRows = report.rows;
          this.reportLoading = false;
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to load the conference report.'));
          this.reportLoading = false;
        }
      })
    );
  }

  exportReport(format: 'Csv' | 'Excel') {
    const submittedFilter = { ...this.reportFilter };
    this.exporting = true;
    this.subs.push(
      this.conferenceEligibilityService.exportReport(submittedFilter, format).subscribe({
        next: blob => {
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = `ConferenceEligibility_${new Date().toISOString().substring(0, 10).replace(/-/g, '')}.${format === 'Csv' ? 'csv' : 'xlsx'}`;
          anchor.click();
          URL.revokeObjectURL(url);
          this.exporting = false;
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, `Unable to export the ${format} report.`));
          this.exporting = false;
        }
      })
    );
  }

  openApplication(application: ConferenceApplicationModel) {
    this.subs.push(
      this.conferenceEligibilityService.getApplication(application.id).subscribe({
        next: detail => {
          this.selectedApplication = detail;
          this.selectedStatusID = null;
          this.notes = '';
          this.isOverride = false;
          this.showDetail = true;
        },
        error: error => this.alertService.error(this.errorMessage(error, 'Unable to load the application.'))
      })
    );
  }

  submitReview() {
    if (!this.selectedStatusID) {
      this.alertService.error('Select a review action.');
      return;
    }
    if ((this.selectedStatusID === ConferenceApplicationStatus.Approved
      || this.selectedStatusID === ConferenceApplicationStatus.Denied) && !this.notes.trim()) {
      this.alertService.error('A decision note is required for approval or denial.');
      return;
    }

    const review: ConferenceApplicationReviewModel = {
      statusID: Number(this.selectedStatusID),
      notes: this.notes.trim(),
      isOverride: this.isOverride
    };
    this.saving = true;
    this.subs.push(
      this.conferenceEligibilityService.reviewApplication(this.selectedApplication.id, review).subscribe({
        next: application => {
          const index = this.applications.findIndex(item => item.id === application.id);
          if (index >= 0) {
            this.applications[index] = application;
          }
          this.selectedApplication = application;
          this.selectedStatusID = null;
          this.notes = '';
          this.isOverride = false;
          this.saving = false;
          this.alertService.success(`Application moved to ${application.statusLabel}.`);
        },
        error: error => {
          this.alertService.error(this.errorMessage(error, 'Unable to update the conference application.'));
          this.saving = false;
        }
      })
    );
  }

  openAnswerFile(answer: ConferenceApplicationAnswerModel) {
    const previewWindow = window.open('about:blank', '_blank');
    if (previewWindow) {
      previewWindow.opener = null;
    }

    this.subs.push(
      this.conferenceEligibilityService.getAnswerFile(answer.id).subscribe({
        next: file => {
          const fileUrl = URL.createObjectURL(file);
          if (previewWindow) {
            previewWindow.location.replace(fileUrl);
          } else {
            const anchor = document.createElement('a');
            anchor.href = fileUrl;
            anchor.target = '_blank';
            anchor.rel = 'noopener';
            anchor.download = answer.fileName || '';
            anchor.click();
          }
          window.setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
        },
        error: error => {
          previewWindow?.close();
          this.alertService.error(this.errorMessage(error, 'Unable to load the application file.'));
        }
      })
    );
  }

  private errorMessage(error: any, fallback: string) {
    return typeof error?.error === 'string'
      ? error.error
      : error?.error?.message || error?.message || fallback;
  }
}
